import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import { TransactionItem } from "Components/Shared/Transactions/TransactionItem/TransactionItem";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { Resources, useResource } from "Translations/Resources";
import { getTransactionDirection } from "Utils/TransactionUtils";
import { useTransactionsRefunds } from "Api/Queries/Transactions/useTransactionsRefunds";
import { type ReactNode, useState } from "react";
import { ButtonLink } from "Components/Shared/ButtonLink";
import { appUrl } from "Utils/UrlUtils";

const DEFAULT_VISIBLE_COUNT = 5;

export const PendingInverseTransactions = () => {
  const user = useLoggedInUser();
  const { data: refundsData } = useTransactionsRefunds({ limit: 100 });
  const { t } = useResource();
  const [isExpanded, setIsExpanded] = useState(false);

  const currentUserID = user?.userID || -1;
  const items = refundsData?.items || [];
  const count = items.length;

  if (count === 0) {
    return null;
  }

  const visibleItems = isExpanded
    ? items
    : items.slice(0, DEFAULT_VISIBLE_COUNT);
  const isExpandable = count > DEFAULT_VISIBLE_COUNT;

  return (
    <Layout count={count}>
      {visibleItems.map((transaction) => (
        <Box
          key={transaction.publicID}
          display="flex"
          alignItems="center"
          gap={3}
        >
          <Box flex={1}>
            <TransactionItem
              item={transaction}
              direction={getTransactionDirection(transaction, currentUserID)}
              isBottomBorderHidden
              isInverseTransaction
            />
          </Box>
          {transaction.debtorID === currentUserID && (
            <ButtonLink
              fullWidth={false}
              variant="outlined"
              color="primary"
              sx={{ height: (theme) => theme.spacing(11) }}
              link={`${appUrl("customer-payment-pay")}/${transaction.publicID}`}
              text={t(Resources.Common.Confirm)}
            />
          )}
        </Box>
      ))}
      {isExpandable && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Button variant="text" onClick={() => setIsExpanded((prev) => !prev)}>
            {isExpanded
              ? t(Resources.Common.ShowLess)
              : t(Resources.Common.ShowMore)}
          </Button>
        </Box>
      )}
    </Layout>
  );
};

type LayoutProps = {
  children: ReactNode;
  count: number;
};

const Layout = ({ children, count }: LayoutProps) => {
  const { t } = useResource();

  return (
    <Stack>
      <PrimaryPaper>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Typography fontWeight={600}>
              {t(Resources.Components.Transactions.PendingRefunds)}
            </Typography>
            <Chip color="error" label={count} sx={{ height: 23 }} />
          </Box>
        </Box>
        {children}
      </PrimaryPaper>
    </Stack>
  );
};
